import { QueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { REGISTRATION_API_URL, USER_QUERY_KEY } from '@/constants';
import { apiUrl } from '@/helpers';
import { ErrorResponse, Options, RegisterCredentials } from '@/types';

/** Separate instance so AuthProvider's Bearer interceptor is not applied. */
const anonymousApi = axios.create();

export type SsoSignInPayload = {
  idToken: string;
  firebaseUid: string;
  providerId: string;
};

export type EntraSignInPayload = {
  idToken: string;
  providerId: string;
  inviteToken?: string;
};

export type SsoInvitePayload = {
  idToken?: string;
  providerId: string;
  firebaseUid?: string;
  inviteToken?: string;
  accessToken?: string;
};

export const registrationEndpoints = {
  register: (payload: RegisterCredentials) =>
    axios.post(`${apiUrl()}${REGISTRATION_API_URL}`, payload),

  prepareSsoInvite: (inviteToken: string) =>
    anonymousApi.post(`${apiUrl()}${REGISTRATION_API_URL}/prepare_sso_invite`, { inviteToken }),

  completeSsoInvite: (payload: SsoInvitePayload) =>
    anonymousApi.post<{
      customToken?: string;
      requiresPasswordLink?: boolean;
      code?: string;
    }>(`${apiUrl()}${REGISTRATION_API_URL}/complete_sso_invite`, {
      idToken: payload.idToken || undefined,
      providerId: payload.providerId,
      firebaseUid: payload.firebaseUid || undefined,
      inviteToken: payload.inviteToken || undefined,
      accessToken: payload.accessToken || undefined
    }),

  validateSsoSignIn: (payload: SsoSignInPayload) =>
    anonymousApi.post(`${apiUrl()}${REGISTRATION_API_URL}/validate_sso_sign_in`, payload),

  acceptEntraSignIn: (payload: EntraSignInPayload) =>
    anonymousApi.post<{
      customToken?: string;
      requiresPasswordLink?: boolean;
      code?: string;
      email?: string;
    }>(`${apiUrl()}${REGISTRATION_API_URL}/accept_entra_sign_in`, {
      idToken: payload.idToken,
      providerId: payload.providerId,
      inviteToken: payload.inviteToken || undefined
    })
};

export const useRegisterMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(registrationEndpoints.register, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([USER_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
